/**
 * Thin wrappers around the Telegram WebApp object that's injected by
 * `https://telegram.org/js/telegram-web-app.js`. We don't pull in the full
 * SDK runtime here because we only need initData + a couple of helpers.
 */

interface TgWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; username?: string; first_name?: string };
    /** Deep-link param from t.me/bot?startapp=<param>. */
    start_param?: string;
  };
  ready: () => void;
  expand: () => void;
  colorScheme: "light" | "dark";
  openLink: (url: string) => void;
  openTelegramLink: (url: string) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export function getTg(): TgWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function getInitData(): string {
  return getTg()?.initData ?? "";
}

export function openChannelLink(url: string) {
  const tg = getTg();
  // Telegram links: use the dedicated helper so the Mini App can navigate
  // in-place instead of bouncing through an external browser.
  if (url.startsWith("https://t.me/") || url.startsWith("tg://")) {
    if (tg) tg.openTelegramLink(url);
    else window.open(url, "_blank");
    return;
  }
  // Plain web links → tg.openLink opens an in-app browser (or external).
  if (url.startsWith("https://") || url.startsWith("http://")) {
    if (tg) tg.openLink(url);
    else window.open(url, "_blank");
    return;
  }
  // Custom URI schemes (viber://, tel:, mailto:) — Telegram's openLink does
  // NOT dispatch these in many clients (it expects https). Let the OS handle
  // the redirect directly via location.href, which both iOS and Android pick
  // up to launch the registered handler app.
  window.location.href = url;
}
